import React, { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { applyTypography } from '@repo/common';

/**
 * @example
 * <PathfinderTextInput
 *   value={details}
 *   onChange={(event) => setDetails(event.target.value)}
 *   placeholder="Write your answer"
 * />
 */
function PathfinderTextInput({
  value,
  defaultValue = '',
  onChange,
  placeholder = 'Write your answer',
  maxLength = 2000,
  minHeight = '236px',
  className = '',
  ...props
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const currentLength = currentValue.length;

  const containerClasses = useMemo(
    () =>
      twMerge(
        'flex w-full flex-col items-start gap-2',
        className
      ),
    [className]
  );

  const fieldClasses = useMemo(
    () =>
      twMerge(
        'flex w-full items-start gap-2 rounded-[15px] border-2 px-4 py-3 transition-colors duration-300 ease-out',
        'bg-surface-lightgray60/60',
        isFocused ? 'border-surface-green40' : 'border-transparent'
      ),
    [isFocused]
  );

  const counterClasses = twMerge(
    applyTypography('b14Strong', `${currentLength}/${maxLength}`),
    'w-full text-right transition-colors duration-300 ease-out',
    isFocused ? 'text-green-deep02' : 'text-text-secondary'
  );

  const handleChange = (event) => {
    const nextValue = event.target.value.slice(0, maxLength);

    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.({
      ...event,
      target: {
        ...event.target,
        value: nextValue,
      },
    });
  };

  return (
    <div className={containerClasses}>
      <div className={fieldClasses}>
        <textarea
          value={currentValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          maxLength={maxLength}
          className={twMerge(
            applyTypography('b16', currentValue || placeholder),
            'min-h-[236px] w-full resize-none border-none bg-transparent p-0 text-text-primary outline-none placeholder:text-text-placeholder'
          )}
          style={{ minHeight }}
          {...props}
        />
      </div>

      <div className={counterClasses}>
        <span className={currentLength === 0 ? 'line-through' : ''}>
          {currentLength}
        </span>
        <span>/{maxLength}</span>
      </div>
    </div>
  );
}

export default PathfinderTextInput;
